import { FC, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { getUrl } from '../../providers/data'
import authToken from '../../utils/authToken'

interface Props {
  open: boolean
  onClose: () => void
  onComplete: () => void
  gemeenteSid: number
  gemeenteNaam: string
  soort: 'stad' | 'gebied'
  onderdeel: string
  onderdeelLabel: string
}

const OverigeDataImportDialog: FC<Props> = ({
  open,
  onClose,
  onComplete,
  gemeenteSid,
  gemeenteNaam,
  soort,
  onderdeel,
  onderdeelLabel,
}) => {
  const [code, setCode] = useState('')
  const [naam, setNaam] = useState('')
  const [memo, setMemo] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<{ imported: number } | null>(null)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      setFile(files[0])
      setError('')
      setResult(null)
    }
  }

  const handleClose = () => {
    if (result) onComplete()
    else onClose()
    setFile(null)
    setCode('')
    setNaam('')
    setMemo('')
    setError('')
    setResult(null)
  }

  const handleImport = async () => {
    if (!file || !code) return
    setLoading(true)
    setError('')
    setResult(null)
    try {
      const params = new URLSearchParams({ code })
      if (naam) params.append('naam', naam)
      if (memo) params.append('memo', memo)
      const formData = new FormData()
      formData.append('file', file)
      const token = authToken.getToken()
      const response = await fetch(
        `${getUrl()}/gemeenten/${gemeenteSid}/overige_data/${soort}/${onderdeel}/import?${params.toString()}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData },
      )
      if (!response.ok) {
        const errorData = (await response.json().catch(() => null)) as
          | { detail?: string }
          | null
        throw new Error(errorData?.detail || `HTTP ${response.status}`)
      }
      const data = (await response.json()) as { imported: number }
      setResult(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import mislukt')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Importeren — {onderdeelLabel} ({gemeenteNaam})
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Code"
            size="small"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            helperText="Identificeert deze import-set. Bestaande rijen voor deze code worden overschreven."
          />
          <TextField
            label="Naam"
            size="small"
            value={naam}
            onChange={(e) => setNaam(e.target.value)}
          />
          <TextField
            label="Korte omschrijving"
            size="small"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
          />

          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            fullWidth
          >
            {file ? file.name : 'Selecteer bestand (.xlsx of .csv)'}
            <input
              type="file"
              hidden
              accept=".csv,.xlsx"
              onChange={handleFileChange}
            />
          </Button>

          <Typography variant="caption" color="textSecondary">
            Verplichte kolommen verschillen per onderdeel — zie de export als template.
          </Typography>

          {loading && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}
          {result && (
            <Alert severity="success">
              {result.imported} records geïmporteerd voor {onderdeelLabel}.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{result ? 'Sluiten' : 'Annuleren'}</Button>
        {!result && (
          <Button
            onClick={() => void handleImport()}
            variant="contained"
            disabled={!file || !code || loading}
          >
            Importeren
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default OverigeDataImportDialog
